import React from 'react';
import { useTranslation } from 'react-i18next';
import { LeadCaptureModal } from '@/components/forms/LeadCaptureModal';
import { Phone, Mail, MapPin, Clock } from 'lucide-react';

export const Contact = () => {
  const { t } = useTranslation();
  const [isModalOpen, setIsModalOpen] = React.useState(true);

  return (
    <div data-testid="contact-page">
      {/* Hero Section */}
      <section className="py-24 bg-[hsl(210,40%,98%)]">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1
            className="text-5xl sm:text-6xl font-bold text-[hsl(215,75%,16%)] mb-6 tracking-tight"
            style={{ fontFamily: 'Outfit' }}
          >
            {t('nav.contact')}
          </h1>
          <p className="text-xl text-gray-600 max-w-2xl mx-auto">
            Get in touch with us today for a free consultation and estimate
          </p>
        </div>
      </section>

      {/* Contact Info & Form */}
      <section className="py-24 bg-white">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
            {/* Contact Information */}
            <div>
              <h2
                className="text-3xl font-bold text-[hsl(215,75%,16%)] mb-8"
                style={{ fontFamily: 'Outfit' }}
              >
                Get In Touch
              </h2>
              <p className="text-lg text-gray-600 mb-8 leading-relaxed">
                We're here to answer your questions and provide you with a free estimate for your project. Contact us today!
              </p>

              <div className="space-y-6">
                {/* Phone Numbers */}
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-[hsl(215,75%,16%)] rounded-full flex items-center justify-center">
                      <Phone className="w-6 h-6 text-white" />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      {t('footer.phone')}
                    </h3>
                    <div className="space-y-1">
                      <a
                        href="tel:+14074510902"
                        className="block text-[hsl(224,64%,33%)] hover:text-[hsl(215,75%,16%)] transition-colors duration-200"
                        data-testid="contact-phone-1"
                      >
                        +1 (407) 451-0902
                      </a>
                      <a
                        href="tel:+14074961867"
                        className="block text-[hsl(224,64%,33%)] hover:text-[hsl(215,75%,16%)] transition-colors duration-200"
                        data-testid="contact-phone-2"
                      >
                        +1 (407) 496-1867
                      </a>
                    </div>
                  </div>
                </div>

                {/* Email */}
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-[hsl(215,75%,16%)] rounded-full flex items-center justify-center">
                      <Mail className="w-6 h-6 text-white" />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      {t('footer.email')}
                    </h3>
                    <a
                      href="mailto:edxconstruction1@gmail.com"
                      className="text-[hsl(224,64%,33%)] hover:text-[hsl(215,75%,16%)] transition-colors duration-200"
                      data-testid="contact-email"
                    >
                      edxconstruction1@gmail.com
                    </a>
                  </div>
                </div>

                {/* Service Area */}
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-[hsl(215,75%,16%)] rounded-full flex items-center justify-center">
                      <MapPin className="w-6 h-6 text-white" />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      Service Area
                    </h3>
                    <p className="text-gray-600">
                      {t('serviceAreas.subtitle')}
                    </p>
                  </div>
                </div>

                {/* Business Hours */}
                <div className="flex items-start space-x-4">
                  <div className="flex-shrink-0">
                    <div className="w-12 h-12 bg-[hsl(215,75%,16%)] rounded-full flex items-center justify-center">
                      <Clock className="w-6 h-6 text-white" />
                    </div>
                  </div>
                  <div>
                    <h3 className="text-lg font-semibold text-gray-900 mb-2">
                      Business Hours
                    </h3>
                    <div className="text-gray-600 space-y-1">
                      <p>Monday - Friday: 8:00 AM - 6:00 PM</p>
                      <p>Saturday: 9:00 AM - 4:00 PM</p>
                      <p>Sunday: Closed</p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* Embedded Lead Form */}
            <div className="bg-[hsl(210,40%,98%)] rounded-lg p-8">
              <h2
                className="text-3xl font-bold text-[hsl(215,75%,16%)] mb-6"
                style={{ fontFamily: 'Outfit' }}
              >
                {t('form.title')}
              </h2>
              <p className="text-gray-600 mb-8">
                Fill out the form below and we'll get back to you as soon as possible.
              </p>
              <button
                onClick={() => setIsModalOpen(true)}
                className="w-full bg-[hsl(215,75%,16%)] text-white hover:bg-[hsl(224,64%,33%)] px-8 py-6 rounded-md font-semibold text-lg transition-colors duration-200"
                data-testid="contact-open-form-button"
              >
                Open Contact Form
              </button>
            </div>
          </div>
        </div>
      </section>

      <LeadCaptureModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
      />
    </div>
  );
};